import { useCallback, useEffect, useRef } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  Edit02Icon,
  Copy01Icon,
  Delete02Icon,
  ClipboardIcon,
  ArrowDataTransferHorizontalIcon,
  ArrowExpandIcon,
  CheckListIcon,
  PlayIcon,
  ArrowDown01Icon,
} from "@hugeicons/core-free-icons"
import { usePipelineStore } from "@/lib/stores/pipeline-store"

interface MenuPosition {
  x: number
  y: number
}

interface CanvasContextMenuProps {
  position: MenuPosition | null
  /** If set, the context menu is for this node. Otherwise it's for the pane. */
  nodeId: string | null
  onClose: () => void
  onAddNode: (position: MenuPosition) => void
  onFitView: () => void
  onRenameNode?: (nodeId: string) => void
  onRunNode?: (nodeId: string) => void
  onRunFromHere?: (nodeId: string) => void
}

interface MenuItemProps {
  icon: typeof Add01Icon
  label: string
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
  shortcut?: string
}

function MenuItem({ icon, label, onClick, disabled, destructive, shortcut }: MenuItemProps) {
  return (
    <button
      className={`flex w-full items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors
        ${disabled ? "opacity-50 pointer-events-none" : ""}
        ${destructive
          ? "text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
          : "text-surface-raised-text hover:bg-surface-raised-hover"
        }`}
      onClick={onClick}
      disabled={disabled}
    >
      <HugeiconsIcon icon={icon} className="size-4 shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      {shortcut && (
        <span className="text-xs text-muted-foreground ml-auto">{shortcut}</span>
      )}
    </button>
  )
}

function Separator() {
  return <div className="bg-black/10 dark:bg-white/10 -mx-1 my-1 h-px" />
}

export function CanvasContextMenu({
  position,
  nodeId,
  onClose,
  onAddNode,
  onFitView,
  onRenameNode,
  onRunNode,
  onRunFromHere,
}: CanvasContextMenuProps) {
  const removeNode = usePipelineStore((s) => s.removeNode)
  const duplicateNode = usePipelineStore((s) => s.duplicateNode)
  const selectNode = usePipelineStore((s) => s.selectNode)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on click outside or Escape
  useEffect(() => {
    if (!position) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [position, onClose])

  const handleDuplicate = useCallback(() => {
    if (!nodeId) return
    const newId = duplicateNode(nodeId)
    if (newId) selectNode(newId)
    onClose()
  }, [nodeId, duplicateNode, selectNode, onClose])

  const handleDelete = useCallback(() => {
    if (!nodeId) return
    removeNode(nodeId)
    onClose()
  }, [nodeId, removeNode, onClose])

  if (!position) return null

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-48 rounded-xl border border-white/10 bg-surface-raised p-1.5 shadow-md animate-in fade-in-0 zoom-in-95 duration-100"
      style={{ left: position.x, top: position.y }}
    >
      {nodeId ? (
        <>
          <MenuItem icon={Edit02Icon} label="Rename" onClick={() => { onRenameNode?.(nodeId); onClose() }} />
          <MenuItem icon={ArrowDataTransferHorizontalIcon} label="Replace" onClick={onClose} disabled />
          <Separator />
          <MenuItem icon={ClipboardIcon} label="Cut" onClick={onClose} disabled />
          <MenuItem icon={Copy01Icon} label="Copy" onClick={onClose} disabled />
          <MenuItem icon={Copy01Icon} label="Duplicate" onClick={handleDuplicate} />
          <Separator />
          <MenuItem icon={PlayIcon} label="Run This Node" onClick={() => { onRunNode?.(nodeId); onClose() }} />
          <MenuItem icon={ArrowDown01Icon} label="Run From Here" onClick={() => { onRunFromHere?.(nodeId); onClose() }} />
          <Separator />
          <MenuItem icon={Delete02Icon} label="Delete" onClick={handleDelete} destructive shortcut="⌫" />
        </>
      ) : (
        <>
          <MenuItem icon={Add01Icon} label="Add Node" onClick={() => { onAddNode(position); onClose() }} shortcut="⌘K" />
          <Separator />
          <MenuItem icon={ClipboardIcon} label="Paste" onClick={onClose} disabled />
          <MenuItem icon={CheckListIcon} label="Select All" onClick={onClose} disabled />
          <Separator />
          <MenuItem icon={ArrowExpandIcon} label="Fit View" onClick={() => { onFitView(); onClose() }} />
        </>
      )}
    </div>
  )
}
